/**
 * @file DataIngest.tsx
 * @description View 3 — Data Ingest.
 *
 * Drag-and-drop zones for the three daily CSV extracts:
 *   1. HIS Export (his_data)
 *   2. Lab TAT Log (lab_data)
 *   3. Bed Occupancy Sheet (bed_data)
 *
 * On submit: parses CSV client-side with PapaParse, sends parsed arrays
 * as JSON to the processUpload Cloud Function, and displays the result.
 */

import React, { useState, useRef, useCallback } from "react";
import Papa from "papaparse";
import {
  Upload,
  CheckCircle,
  XCircle,
  FileText,
  Loader2,
  AlertTriangle,
  X,
  Keyboard,
  FilePlus,
  Plus
} from "lucide-react";
import { db } from "../../firebase";
import { writeBatch, doc, collection } from "firebase/firestore";
import { runReconciliation } from "../../lib/ReconciliationEngine";
import { IngestPayload, IngestResponse, RawHIS, RawLab, RawBed } from "../../types";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type FileSlot = "his" | "lab" | "bed";

interface SlotState {
  file: File | null;
  parsed: unknown[] | null;
  error: string | null;
  rowCount: number;
  mode: "file" | "manual";
  manualData: any;
}

const INITIAL_SLOT: SlotState = {
  file: null,
  parsed: null,
  error: null,
  rowCount: 0,
  mode: "file",
  manualData: {},
};

// ─────────────────────────────────────────────────────────────────────────────
// CSV Parser
// ─────────────────────────────────────────────────────────────────────────────

function parseCSV(file: File): Promise<{ data: unknown[]; error?: string }> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: (results) => {
        if (results.errors.length > 0 && results.data.length === 0) {
          resolve({ data: [], error: results.errors[0].message });
        } else {
          resolve({ data: results.data });
        }
      },
      error: (err) => resolve({ data: [], error: err.message }),
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Drop Zone Component
// ─────────────────────────────────────────────────────────────────────────────

const SLOT_CONFIG: Record<
  FileSlot,
  { label: string; desc: string; requiredColumns: string[]; color: string; borderColor: string }
> = {
  his: {
    label: "HIS Export",
    desc: "Hospital Information System — admissions & discharge records",
    requiredColumns: ["patient_id", "clinical_status", "assigned_bed"],
    color: "text-blue-400",
    borderColor: "border-blue-500/30",
  },
  lab: {
    label: "Lab TAT Log",
    desc: "Order-to-result turnaround time log from the Lab system",
    requiredColumns: ["order_id", "patient_id", "result_status", "tat_minutes"],
    color: "text-teal-400",
    borderColor: "border-teal-500/30",
  },
  bed: {
    label: "Bed Occupancy Sheet",
    desc: "Manually maintained physical bed status sheet",
    requiredColumns: ["bed_id", "physical_status"],
    color: "text-amber-400",
    borderColor: "border-amber-500/30",
  },
};

interface DropZoneProps {
  slot: FileSlot;
  state: SlotState;
  onFile: (slot: FileSlot, file: File) => void;
  onClear: (slot: FileSlot) => void;
  onModeChange: (slot: FileSlot, mode: "file" | "manual") => void;
  onManualChange: (slot: FileSlot, data: any) => void;
  onAddManual: (slot: FileSlot) => void;
}

function DropZone({ slot, state, onFile, onClear, onModeChange, onManualChange, onAddManual }: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cfg = SLOT_CONFIG[slot];

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) onFile(slot, file);
    },
    [slot, onFile]
  );

  
  const isLoaded = (state.mode === "file" && !!state.file && !state.error) || (state.mode === "manual" && state.rowCount > 0);
  const hasError = !!state.error;

  const renderManualForm = () => {
    if (slot === "his") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 animate-fade-in p-4 bg-white/5 rounded-xl border border-white/5">
           <input type="text" placeholder="Patient ID" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.patient_id || ""} onChange={e => onManualChange(slot, {...state.manualData, patient_id: e.target.value})} />
           <input type="text" placeholder="Patient Name" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.patient_name || ""} onChange={e => onManualChange(slot, {...state.manualData, patient_name: e.target.value})} />
           <input type="text" placeholder="Clinical Status" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.clinical_status || ""} onChange={e => onManualChange(slot, {...state.manualData, clinical_status: e.target.value})} />
           <input type="text" placeholder="Assigned Bed" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.assigned_bed || ""} onChange={e => onManualChange(slot, {...state.manualData, assigned_bed: e.target.value})} />
           <button onClick={(e) => { e.stopPropagation(); onAddManual(slot); }} className="sm:col-span-3 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white text-xs font-bold py-2 rounded-lg mt-2 transition-colors"><Plus className="w-3.5 h-3.5" /> Add Row Manually</button>
        </div>
      );
    }
    if (slot === "lab") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 animate-fade-in p-4 bg-white/5 rounded-xl border border-white/5">
           <input type="text" placeholder="Order ID" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.order_id || ""} onChange={e => onManualChange(slot, {...state.manualData, order_id: e.target.value})} />
           <input type="text" placeholder="Patient ID" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.patient_id || ""} onChange={e => onManualChange(slot, {...state.manualData, patient_id: e.target.value})} />
           <input type="text" placeholder="Result Status" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.result_status || ""} onChange={e => onManualChange(slot, {...state.manualData, result_status: e.target.value})} />
           <input type="number" placeholder="TAT Minutes" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.tat_minutes || ""} onChange={e => onManualChange(slot, {...state.manualData, tat_minutes: e.target.value})} />
           <button onClick={(e) => { e.stopPropagation(); onAddManual(slot); }} className="sm:col-span-3 flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-white text-xs font-bold py-2 rounded-lg mt-2 transition-colors"><Plus className="w-3.5 h-3.5" /> Add Row Manually</button>
        </div>
      );
    }
    if (slot === "bed") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 animate-fade-in p-4 bg-white/5 rounded-xl border border-white/5">
           <input type="text" placeholder="Bed ID" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.bed_id || ""} onChange={e => onManualChange(slot, {...state.manualData, bed_id: e.target.value})} />
           <input type="text" placeholder="Physical Status" className="bg-[#071428] border border-white/10 text-white text-xs rounded-lg px-3 py-2" value={state.manualData.physical_status || ""} onChange={e => onManualChange(slot, {...state.manualData, physical_status: e.target.value})} />
           <button onClick={(e) => { e.stopPropagation(); onAddManual(slot); }} className="sm:col-span-3 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold py-2 rounded-lg mt-2 transition-colors"><Plus className="w-3.5 h-3.5" /> Add Row Manually</button>
        </div>
      );
    }
  };

  return (
    <div
      className={`relative rounded-2xl border-2 transition-all duration-200 ${
        isDragging
          ? "border-teal-500/60 bg-teal-500/8 scale-[1.01]"
          : isLoaded
          ? "border-green-500/40 bg-green-500/5"
          : hasError
          ? "border-red-500/40 bg-red-500/5"
          : `${cfg.borderColor} bg-white/3 hover:bg-white/5`
      } ${state.mode === "file" ? "cursor-pointer" : ""}`}
      onDragOver={(e) => { if(state.mode === "file") { e.preventDefault(); setIsDragging(true); } }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => state.mode === "file" && !isLoaded && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className="hidden"
        id={`file-input-${slot}`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(slot, file);
        }}
      />

      {/* Mode Toggles */}
      <div className="absolute top-4 right-4 flex gap-1 bg-[#040d1a] border border-white/10 rounded-lg p-1 z-10" onClick={e => e.stopPropagation()}>
         <button onClick={() => onModeChange(slot, "file")} className={`px-3 py-1 text-xs rounded-md flex items-center gap-1.5 transition-colors ${state.mode === "file" ? "bg-white/10 text-white" : "text-gray-500 hover:text-gray-300"}`}>
           <FilePlus className="w-3.5 h-3.5" /> CSV
         </button>
         <button onClick={() => onModeChange(slot, "manual")} className={`px-3 py-1 text-xs rounded-md flex items-center gap-1.5 transition-colors ${state.mode === "manual" ? "bg-white/10 text-white" : "text-gray-500 hover:text-gray-300"}`}>
           <Keyboard className="w-3.5 h-3.5" /> Manual
         </button>
      </div>

      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
            isLoaded
              ? "bg-green-500/15"
              : hasError
              ? "bg-red-500/15"
              : "bg-white/5"
          }`}>
            {isLoaded ? (
              <CheckCircle className="w-6 h-6 text-green-400" />
            ) : hasError ? (
              <XCircle className="w-6 h-6 text-red-400" />
            ) : (
              <FileText className={`w-6 h-6 ${cfg.color}`} />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pr-24">
            <div className="flex items-center gap-3">
              <h3 className={`font-semibold text-sm ${cfg.color}`}>{cfg.label}</h3>
              {isLoaded && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClear(slot); }}
                  className="text-gray-600 hover:text-red-400 transition-colors p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-0.5">{cfg.desc}</p>

            {state.mode === "file" && !state.file && !hasError && (
              <div className="mt-3 flex items-center gap-2">
                <Upload className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-gray-600 text-xs">
                  Drag & drop a CSV file, or click to browse
                </span>
              </div>
            )}

            {state.mode === "file" && isLoaded && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="text-white text-xs font-medium truncate max-w-[200px]">
                  {state.file?.name}
                </span>
                <span className="text-green-400 text-xs font-mono">
                  {state.rowCount} rows parsed ✓
                </span>
              </div>
            )}

            {state.mode === "manual" && (
              <div className="mt-3 flex items-center gap-2">
                 {state.rowCount > 0 ? (
                    <span className="text-green-400 text-xs font-mono">{state.rowCount} manual rows added ✓</span>
                 ) : (
                    <span className="text-gray-500 text-xs">Enter data manually below</span>
                 )}
              </div>
            )}

            {hasError && (
              <div className="mt-3 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-red-400 text-xs">{state.error}</span>
              </div>
            )}
          </div>
        </div>
        
        {state.mode === "manual" && renderManualForm()}

        {/* Required columns */}
        <div className="mt-4 pt-3 border-t border-white/5">
          <span className="text-xs text-gray-600 uppercase tracking-wider">
            Required columns:{" "}
          </span>
          {cfg.requiredColumns.map((col) => (
            <code key={col} className="text-xs font-mono text-gray-500 mr-2">
              {col}
            </code>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function DataIngest() {
  const [slots, setSlots] = useState<Record<FileSlot, SlotState>>({
    his: { ...INITIAL_SLOT },
    lab: { ...INITIAL_SLOT },
    bed: { ...INITIAL_SLOT },
  });
  const [batchLabel, setBatchLabel] = useState(
    `Upload ${new Date().toLocaleDateString()}`
  );
  const [submitting, setSubmitting] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [result, setResult] = useState<IngestResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleFile = useCallback(async (slot: FileSlot, file: File) => {
    setSlots((prev) => ({
      ...prev,
      [slot]: { file, parsed: null, error: null, rowCount: 0 },
    }));

    const { data, error } = await parseCSV(file);
    setSlots((prev) => ({
      ...prev,
      [slot]: {
        file,
        parsed: data,
        error: error ?? null,
        rowCount: data.length,
      },
    }));
  }, []);

  const handleModeChange = useCallback((slot: FileSlot, mode: "file" | "manual") => {
    setSlots((prev) => ({ ...prev, [slot]: { ...prev[slot], mode, parsed: mode === "manual" ? (prev[slot].parsed || []) : null, rowCount: mode === "manual" ? (prev[slot].parsed ? prev[slot].parsed.length : 0) : 0, error: null } }));
  }, []);

  const handleManualChange = useCallback((slot: FileSlot, data: any) => {
    setSlots((prev) => ({ ...prev, [slot]: { ...prev[slot], manualData: data } }));
  }, []);

  const handleAddManual = useCallback((slot: FileSlot) => {
    setSlots((prev) => {
       const newParsed = [...(prev[slot].parsed || []), prev[slot].manualData];
       return { ...prev, [slot]: { ...prev[slot], parsed: newParsed, rowCount: newParsed.length, manualData: {} } };
    });
  }, []);

  const handleClear = useCallback((slot: FileSlot) => {
    setSlots((prev) => ({ ...prev, [slot]: { ...INITIAL_SLOT } }));
    setResult(null);
  }, []);

  const resetAll = useCallback(() => {
    setSlots({
      his: { ...INITIAL_SLOT },
      lab: { ...INITIAL_SLOT },
      bed: { ...INITIAL_SLOT },
    });
    setBatchLabel(`Upload ${new Date().toLocaleDateString()}`);
    setResult(null);
    setSubmitError(null);
  }, []);

  const canSubmit =
    slots.his.parsed &&
    slots.lab.parsed &&
    slots.bed.parsed &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setProgressText("Initializing engine...");
    setSubmitError(null);
    setResult(null);

    try {
      const payload: IngestPayload = {
        his_data: slots.his.parsed as RawHIS[],
        lab_data: slots.lab.parsed as RawLab[],
        bed_data: slots.bed.parsed as RawBed[],
        batch_label: batchLabel,
      };

      // ── 1. Run the reconciliation engine locally ──
      setProgressText("Reconciling across 3 sources...");
      // Add artificial delay for UX feel (users trust it more if it feels like it's "working")
      await new Promise(r => setTimeout(r, 600)); 

      const { unifiedRecords, auditLog, metrics, reconciliation_run_id } =
        runReconciliation(
          payload.his_data,
          payload.lab_data,
          payload.bed_data
        );

      // ── 2. Batch write to Firestore ──
      setProgressText("Writing to live database...");
      const BATCH_SIZE = 450;
      const sanitizeId = (id: string) => id.replace(/[/\\]/g, "_");

      function chunk<T>(arr: T[], size: number): T[][] {
        const result: T[][] = [];
        for (let i = 0; i < arr.length; i += size) {
          result.push(arr.slice(i, i + size));
        }
        return result;
      }

      // Write unified_operations_view
      const unifiedChunks = chunk(unifiedRecords, BATCH_SIZE);
      for (const batchRecords of unifiedChunks) {
        const batch = writeBatch(db);
        for (const record of batchRecords) {
          const docRef = doc(db, "unified_operations_view", sanitizeId(record.patient_id));
          batch.set(docRef, record);
        }
        await batch.commit();
      }

      // Write reconciliation_audit_log
      setProgressText("Generating audit logs...");
      const auditChunks = chunk(auditLog, BATCH_SIZE);
      for (const batchEntries of auditChunks) {
        const batch = writeBatch(db);
        for (const entry of batchEntries) {
          const docRef = doc(collection(db, "reconciliation_audit_log"));
          batch.set(docRef, entry);
        }
        await batch.commit();
      }

      // Write system_metrics
      setProgressText("Finalizing system metrics...");
      const metricsRef = doc(db, "system_metrics", "latest");
      const historicalMetricsRef = doc(db, "system_metrics", reconciliation_run_id);
      
      const metricsBatch = writeBatch(db);
      metricsBatch.set(metricsRef, metrics);
      metricsBatch.set(historicalMetricsRef, metrics);
      await metricsBatch.commit();

      setResult({
        success: true,
        reconciliation_run_id,
        records_written: unifiedRecords.length,
        conflicts_found: auditLog.length,
        errors: [],
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to process data locally."
      );
    } finally {
      setSubmitting(false);
      setProgressText("");
    }
  };

  const totalRows =
    (slots.his.rowCount || 0) +
    (slots.lab.rowCount || 0) +
    (slots.bed.rowCount || 0);

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl pb-12">

      {/* Header info */}
      <div
        className="rounded-2xl border border-teal-500/20 p-5 backdrop-blur-md"
        style={{ background: "rgba(20, 184, 166, 0.05)" }}
      >
        <div className="flex items-start gap-3">
          <Upload className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="text-white font-semibold text-sm">How to use this page</h2>
            <p className="text-gray-400 text-sm mt-1 leading-relaxed">
              Upload the three daily export files below. Each file is parsed locally in your browser,
              then sent to the reconciliation engine which will merge all three sources,
              apply the 4 conflict rules, and update the live dashboard automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Drop zones */}
      <div className="space-y-4">
        {(["his", "lab", "bed"] as FileSlot[]).map((slot) => (
          <DropZone
            key={slot}
            slot={slot}
            state={slots[slot]}
            onFile={handleFile}
            onClear={handleClear}
            onModeChange={handleModeChange}
            onManualChange={handleManualChange}
            onAddManual={handleAddManual}
          />
        ))}
      </div>

      {/* Batch label + submit */}
      <div
        className="rounded-2xl border border-white/8 p-6"
        style={{
          background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
        }}
      >
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">
              Batch Label (optional)
            </label>
            <input
              id="batch-label-input"
              type="text"
              value={batchLabel}
              onChange={(e) => setBatchLabel(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm
                         focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              placeholder="e.g. 2024-08-01 Morning Upload"
            />
          </div>

          <button
            id="submit-upload-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center justify-center w-full sm:w-auto min-w-[220px] gap-2.5 px-6 py-2.5 rounded-xl bg-gradient-teal text-white font-semibold text-sm
                       transition-all duration-200 hover:shadow-glow active:scale-[0.99]
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none flex-shrink-0"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {progressText || "Processing..."}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Run Reconciliation ({totalRows} rows)
              </>
            )}
          </button>
        </div>

        {/* Progress summary */}
        <div className="mt-4 flex gap-3 flex-wrap">
          {(["his", "lab", "bed"] as FileSlot[]).map((slot) => (
            <div key={slot} className="flex items-center gap-1.5">
              {slots[slot].parsed ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-gray-600" />
              )}
              <span className="text-xs text-gray-500">
                {SLOT_CONFIG[slot].label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div
          className={`rounded-2xl border p-6 shadow-2xl animate-slide-in backdrop-blur-md ${
            result.success
              ? "border-green-500/30 bg-gradient-to-r from-green-500/10 to-green-500/5"
              : "border-red-500/30 bg-red-500/8"
          }`}
        >
          {result.success ? (
            <div className="flex flex-col sm:flex-row items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <div className="text-green-400 font-bold text-lg">Reconciliation Complete!</div>
                  <p className="text-gray-300 text-sm mt-1.5 leading-relaxed">
                    Successfully parsed <strong className="text-white">{totalRows}</strong> total rows across 3 systems. <br/>
                    Generated <strong className="text-white">{result.records_written}</strong> unified patient records and found <strong className="text-white">{result.conflicts_found}</strong> active conflicts.
                  </p>
                  <div className="text-gray-500 text-xs mt-3 font-mono bg-black/20 inline-block px-2 py-1 rounded">
                    Run ID: {result.reconciliation_run_id}
                  </div>
                </div>
              </div>
              <button 
                onClick={resetAll}
                className="px-5 py-2 rounded-lg border border-green-500/30 text-green-400 hover:bg-green-500/10 text-sm font-medium transition-colors whitespace-nowrap"
              >
                Upload another batch
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-red-400 font-semibold text-sm">Processing Failed</div>
                <p className="text-gray-300 text-sm mt-1 whitespace-pre-wrap">
                  {submitError}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {submitError && !result && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/8 p-5 animate-slide-in backdrop-blur-md">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{submitError}</p>
          </div>
        </div>
      )}
    </div>
  );
}
